<template>
  <div class="storage-dropdown">
    <div
      v-if="label"
      class="storage-dropdown__label"
    >
      {{ label }}
      <InfoIcon class="storage-dropdown__label--info">
        <template #tooltip-content> Region identifier for the S3 bucket. </template>
      </InfoIcon>
    </div>
    <div class="storage-dropdown__select">
      <SelectField
        :value="value"
        :options="options[provider]"
        size="md"
        placeholder="Select value"
        :show-icon-prepend="false"
        :aria-label="label"
        @change="onSelect"
      >
        <template #option="{ option, selected }">
          <ListItemWithState
            :selected="selected"
            size="md"
            wrap
            :aria-label="option.text"
          >
            {{ option.text }}
          </ListItemWithState>
        </template>
      </SelectField>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'S3RegionSelect',
}
</script>

<script lang="ts" setup>
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'
import SelectField from '@/uiKit/SelectField/SelectField.vue'
import type { OptionType } from '@/uiKit/types'
import InfoIcon from '@/uiKit/InfoIcon/InfoIcon.vue'

withDefaults(
  defineProps<{
    value: string | OptionType[]
    label?: string
    provider: 'aws' | 'wasabi'
  }>(),
  {
    label: '',
  },
)
const emit = defineEmits<{
  (e: 'input', value: OptionType['id']): void
}>()

const awsOptions: OptionType[] = [
  { text: 'us-east-2', id: 'us-east-2' },
  { text: 'us-east-1', id: 'us-east-1' },
  { text: 'us-west-1', id: 'us-west-1' },
  { text: 'us-west-2', id: 'us-west-2' },
  { text: 'af-south-1', id: 'af-south-1' },
  { text: 'ap-east-1', id: 'ap-east-1' },
  { text: 'ap-south-1', id: 'ap-south-1' },
  { text: 'ap-south-2', id: 'ap-south-2' },
  { text: 'ap-northeast-3', id: 'ap-northeast-3' },
  { text: 'ap-northeast-2', id: 'ap-northeast-2' },
  { text: 'ap-southeast-1', id: 'ap-southeast-1' },
  { text: 'ap-southeast-2', id: 'ap-southeast-2' },
  { text: 'ap-southeast-3', id: 'ap-southeast-3' },
  { text: 'ap-southeast-4', id: 'ap-southeast-4' },
  { text: 'ap-northeast-1', id: 'ap-northeast-1' },
  { text: 'ca-central-1', id: 'ca-central-1' },
  { text: 'cn-north-1', id: 'cn-north-1' },
  { text: 'cn-northwest-1', id: 'cn-northwest-1' },
  { text: 'eu-central-1', id: 'eu-central-1' },
  { text: 'eu-central-2', id: 'eu-central-2' },
  { text: 'eu-west-1', id: 'eu-west-1' },
  { text: 'eu-west-2', id: 'eu-west-2' },
  { text: 'eu-south-1', id: 'eu-south-1' },
  { text: 'eu-south-2', id: 'eu-south-2' },
  { text: 'eu-west-3', id: 'eu-west-3' },
  { text: 'eu-north-1', id: 'eu-north-1' },
  { text: 'me-south-1', id: 'me-south-1' },
  { text: 'me-central-1', id: 'me-central-1' },
  { text: 'sa-east-1', id: 'sa-east-1' },
  { text: 'il-central-1', id: 'il-central-1' },
]

const wasabiOptions: OptionType[] = [
  { text: 'us-west-1', id: 'us-west-1' },
  { text: 'us-east-1', id: 'us-east-1' },
  { text: 'us-east-2', id: 'us-east-2' },
  { text: 'us-central-1', id: 'us-central-1' },
  { text: 'ca-central-1', id: 'ca-central-1' },
  { text: 'eu-west-1', id: 'eu-west-1' },
  { text: 'eu-west-2', id: 'eu-west-2' },
  { text: 'eu-central-1', id: 'eu-central-1' },
  { text: 'eu-central-2', id: 'eu-central-2' },
  { text: 'ap-northeast-1', id: 'ap-northeast-1' },
  { text: 'ap-northeast-2', id: 'ap-northeast-2' },
  { text: 'ap-southeast-1', id: 'ap-southeast-1' },
  { text: 'ap-southeast-2', id: 'ap-southeast-2' },
]

const options = {
  aws: awsOptions,
  wasabi: wasabiOptions,
}

const onSelect = (option: OptionType): void => emit('input', option.id)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/spacings.scss';
@import '@/uiKit/assets/typography.scss';

.storage-dropdown {
  position: relative;
  width: 100%;
  cursor: pointer;
}

.storage-dropdown__label {
  display: flex;
  @include typographyRegularBody200;
  color: var(--color-text-subtle);
  margin-bottom: 4px;

  &--info {
    margin-left: 4px;
  }
}

.storage-dropdown__select {
  width: 100%;
}
</style>
